import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, X, ImageIcon, Plus, Trash2, Upload, Save,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  useGetProductQuery,
  useGetActiveCategoriesQuery,
  useUpdateProductMutation,
} from '../../app/api';

const ProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ RTK Query
  const { data: prodData, isLoading: prodLoading } = useGetProductQuery(id);
  const { data: catData } = useGetActiveCategoriesQuery({ limit: 100 });
  const [updateProduct, { isLoading: saving }] = useUpdateProductMutation();

  const categories = catData?.data?.categories || [];

  const [imageBaseUrl, setImageBaseUrl] = useState('');
  const [formData, setFormData] = useState({
    name: '', slug: '', sku: '', shortDescription: '', description: '',
    brand: '', category_id: '', tags: '', collectionName: '',
    mrp: '', price: '', discount: '', discountType: 'percentage',
    costPrice: '', tax: '', taxIncluded: true, currency: 'INR',
    stock: '', lowStockAlert: 5, stockStatus: 'in_stock', maxOrderQuantity: 10,
    image: null, imagePreview: null, existingImage: null,
    gallery: [], galleryPreviews: [], existingGallery: [],
    video: '', thumbnail: null, thumbnailPreview: null, existingThumbnail: null,
    hasVariants: false, variants: [], attributes: [],
    weight: '', dimensions: { length: 0, width: 0, height: 0 },
    shippingCharge: 0, freeShipping: false,
    deliveryTime: '3-5 days', shippingType: 'standard',
    returnable: true, returnDays: 7, replacement: true,
    warranty: '', warrantyPeriod: 0,
    sellerName: '', isCashOnDelivery: true,
    metaTitle: '', metaDescription: '', metaKeywords: '',
    isActive: true, isFeatured: false, isTrending: false, isNewArrival: false,
    isBestSeller: false, isRecommended: false,
    status: 'published', approvedByAdmin: false,
    offerText: '', couponCode: '',
    flashSale: { isActive: false, startDate: '', endDate: '', salePrice: 0 },
    manufacturerName: '', manufacturerAddress: '',
    countryOfOrigin: 'India', hsnCode: '',
  });
  const [errors, setErrors] = useState({});

  // ✅ Prefill form from RTK Query data
  useEffect(() => {
    if (prodData?.data) {
      const { product, imageBaseUrl: baseUrl } = prodData.data;
      setImageBaseUrl(baseUrl || '');

      setFormData({
        name: product.name || '',
        slug: product.slug || '',
        sku: product.sku || '',
        shortDescription: product.shortDescription || '',
        description: product.description || '',
        brand: product.brand || '',
        category_id: product.category?._id || product.category || '',
        tags: (product.tags || []).join(', '),
        collectionName: product.collectionName || '',
        mrp: product.mrp || '',
        price: product.price || '',
        discount: product.discount || '',
        discountType: product.discountType || 'percentage',
        costPrice: product.costPrice || '',
        tax: product.tax || '',
        taxIncluded: product.taxIncluded !== false,
        currency: product.currency || 'INR',
        stock: product.stock || '',
        lowStockAlert: product.lowStockAlert || 5,
        stockStatus: product.stockStatus || 'in_stock',
        maxOrderQuantity: product.maxOrderQuantity || 10,
        image: null,
        imagePreview: null,
        existingImage: product.image || null,
        gallery: [],
        galleryPreviews: [],
        existingGallery: product.gallery || [],
        video: product.video || '',
        thumbnail: null,
        thumbnailPreview: null,
        existingThumbnail: product.thumbnail || null,
        hasVariants: product.hasVariants || false,
        variants: product.variants || [],
        attributes: product.attributes || [],
        weight: product.weight || '',
        dimensions: product.dimensions || { length: 0, width: 0, height: 0 },
        shippingCharge: product.shippingCharge || 0,
        freeShipping: product.freeShipping || false,
        deliveryTime: product.deliveryTime || '3-5 days',
        shippingType: product.shippingType || 'standard',
        returnable: product.returnable !== false,
        returnDays: product.returnDays || 7,
        replacement: product.replacement !== false,
        warranty: product.warranty || '',
        warrantyPeriod: product.warrantyPeriod || 0,
        sellerName: product.sellerName || '',
        isCashOnDelivery: product.isCashOnDelivery !== false,
        metaTitle: product.metaTitle || '',
        metaDescription: product.metaDescription || '',
        metaKeywords: (product.metaKeywords || []).join(', '),
        isActive: product.isActive !== false,
        isFeatured: product.isFeatured || false,
        isTrending: product.isTrending || false,
        isNewArrival: product.isNewArrival || false,
        isBestSeller: product.isBestSeller || false,
        isRecommended: product.isRecommended || false,
        status: product.status || 'published',
        approvedByAdmin: product.approvedByAdmin || false,
        offerText: product.offerText || '',
        couponCode: product.couponCode || '',
        flashSale: product.flashSale || { isActive: false, startDate: '', endDate: '', salePrice: 0 },
        manufacturerName: product.manufacturerName || '',
        manufacturerAddress: product.manufacturerAddress || '',
        countryOfOrigin: product.countryOfOrigin || 'India',
        hsnCode: product.hsnCode || '',
      });
    }
  }, [prodData]);

  const getFullImage = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${imageBaseUrl}${path}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData((p) => ({ ...p, [parent]: { ...p[parent], [field]: value } }));
  };

  const validateFile = (file) => {
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WEBP allowed');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB');
      return false;
    }
    return true;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;
    const r = new FileReader();
    r.onloadend = () =>
      setFormData((prev) => ({ ...prev, image: file, imagePreview: r.result, existingImage: null }));
    r.readAsDataURL(file);
  };

  const removeImage = () =>
    setFormData((prev) => ({ ...prev, image: null, imagePreview: null, existingImage: null }));

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files).filter(validateFile);
    if (!files.length) return;
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve({ file, preview: r.result });
            r.readAsDataURL(file);
          })
      )
    ).then((results) => {
      setFormData((prev) => ({
        ...prev,
        gallery: [...prev.gallery, ...results.map((r) => r.file)],
        galleryPreviews: [...prev.galleryPreviews, ...results.map((r) => r.preview)],
      }));
    });
  };

  const removeNewGalleryImage = (index) =>
    setFormData((prev) => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index),
      galleryPreviews: prev.galleryPreviews.filter((_, i) => i !== index),
    }));

  const removeExistingGalleryImage = (index) =>
    setFormData((prev) => ({
      ...prev,
      existingGallery: prev.existingGallery.filter((_, i) => i !== index),
    }));

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file || !validateFile(file)) return;
    const r = new FileReader();
    r.onloadend = () =>
      setFormData((prev) => ({ ...prev, thumbnail: file, thumbnailPreview: r.result, existingThumbnail: null }));
    r.readAsDataURL(file);
  };

  const removeThumbnail = () =>
    setFormData((prev) => ({ ...prev, thumbnail: null, thumbnailPreview: null, existingThumbnail: null }));

  const addVariant = () =>
    setFormData((p) => ({
      ...p,
      variants: [...p.variants, { variantName: '', variantValue: '', price: '', stock: '', sku: '' }],
    }));
  const updateVariant = (i, f, v) =>
    setFormData((p) => ({ ...p, variants: p.variants.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)) }));
  const removeVariant = (i) =>
    setFormData((p) => ({ ...p, variants: p.variants.filter((_, idx) => idx !== i) }));

  const addAttribute = () =>
    setFormData((p) => ({ ...p, attributes: [...p.attributes, { key: '', value: '' }] }));
  const updateAttribute = (i, f, v) =>
    setFormData((p) => ({ ...p, attributes: p.attributes.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)) }));
  const removeAttribute = (i) =>
    setFormData((p) => ({ ...p, attributes: p.attributes.filter((_, idx) => idx !== i) }));

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Product name is required';
    if (!formData.category_id) e.category_id = 'Category is required';
    if (!formData.description.trim()) e.description = 'Description is required';
    if (!formData.sku.trim()) e.sku = 'SKU is required';
    if (!formData.price || Number(formData.price) <= 0) e.price = 'Valid price required';
    if (formData.mrp && Number(formData.mrp) < Number(formData.price)) e.mrp = 'MRP must be >= price';
    if (!formData.stock || Number(formData.stock) < 0) e.stock = 'Valid stock required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the errors');
      const firstError = Object.keys(newErrors)[0];
      const el = document.querySelector(`[name="${firstError}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      const fd = new FormData();

      ['name', 'slug', 'sku', 'shortDescription', 'description', 'brand', 'collectionName'].forEach((k) =>
        fd.append(k, formData[k] || '')
      );
      fd.append('category_id', formData.category_id);
      if (formData.tags) {
        const tagsArr = formData.tags.split(',').map((t) => t.trim()).filter(Boolean);
        fd.append('tags', JSON.stringify(tagsArr));
      }

      fd.append('mrp', formData.mrp || 0);
      fd.append('price', formData.price);
      fd.append('discount', formData.discount || 0);
      fd.append('discountType', formData.discountType);
      fd.append('costPrice', formData.costPrice || 0);
      fd.append('tax', formData.tax || 0);
      fd.append('taxIncluded', formData.taxIncluded);
      fd.append('currency', formData.currency);

      fd.append('stock', formData.stock);
      fd.append('lowStockAlert', formData.lowStockAlert);
      fd.append('stockStatus', formData.stockStatus);
      fd.append('maxOrderQuantity', formData.maxOrderQuantity);

      if (formData.image) fd.append('image', formData.image);
      formData.gallery.forEach((f) => fd.append('gallery', f));
      fd.append('existingGallery', JSON.stringify(formData.existingGallery));
      if (formData.thumbnail) fd.append('thumbnail', formData.thumbnail);
      fd.append('video', formData.video || '');

      fd.append('hasVariants', formData.hasVariants);
      if (formData.variants.length) {
        const cleaned = formData.variants.filter((v) => v.variantName && v.variantValue);
        fd.append('variants', JSON.stringify(cleaned));
      }
      if (formData.attributes.length) {
        const cleaned = formData.attributes.filter((a) => a.key && a.value);
        fd.append('attributes', JSON.stringify(cleaned));
      }

      fd.append('weight', formData.weight || 0);
      fd.append('dimensions', JSON.stringify(formData.dimensions));
      fd.append('shippingCharge', formData.shippingCharge);
      fd.append('freeShipping', formData.freeShipping);
      fd.append('deliveryTime', formData.deliveryTime);
      fd.append('shippingType', formData.shippingType);

      fd.append('returnable', formData.returnable);
      fd.append('returnDays', formData.returnDays);
      fd.append('replacement', formData.replacement);
      fd.append('warranty', formData.warranty || '');
      fd.append('warrantyPeriod', formData.warrantyPeriod);

      fd.append('sellerName', formData.sellerName || '');
      fd.append('isCashOnDelivery', formData.isCashOnDelivery);

      fd.append('metaTitle', formData.metaTitle || '');
      fd.append('metaDescription', formData.metaDescription || '');
      if (formData.metaKeywords) {
        const arr = formData.metaKeywords.split(',').map((k) => k.trim()).filter(Boolean);
        fd.append('metaKeywords', JSON.stringify(arr));
      }

      [
        'isActive', 'isFeatured', 'isTrending', 'isNewArrival',
        'isBestSeller', 'isRecommended', 'approvedByAdmin',
      ].forEach((k) => fd.append(k, formData[k]));
      fd.append('status', formData.status);

      fd.append('offerText', formData.offerText || '');
      fd.append('couponCode', formData.couponCode || '');
      fd.append('flashSale', JSON.stringify(formData.flashSale));

      ['manufacturerName', 'manufacturerAddress', 'countryOfOrigin', 'hsnCode'].forEach((k) =>
        fd.append(k, formData[k] || '')
      );

      await updateProduct({ id, formData: fd }).unwrap();
      toast.success('Product updated successfully!');
      navigate('/products');
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to update product');
    }
  };

  if (prodLoading) {
    return (
      <Layout>
        <div className="flex-center" style={{ height: '60vh' }}>
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-product-page">
        <div className="page-header">
          <div>
            <Link
              to="/products"
              className="btn btn-sm btn-outline btn-back"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}
            >
              <ArrowLeft size={16} />
              <span>Back to Products</span>
            </Link>
            <h1 className="page-title">Edit Product</h1>
            <p className="page-subtitle">
              Update product information — <strong>{formData.name}</strong>
            </p>
          </div>
        </div>

        {/* SAME FORM AS ProductAdd — copy the whole <form> from ProductAdd.jsx */}
        <div className="card">
          <form onSubmit={handleSubmit}>
            {/* ... paste the same form from ProductAdd above ... */}
            {/* Just change submit button text to "Update Product" and use `saving` instead of `loading` */}
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default ProductEdit;